<template>
  <div
    class="tab-pane fade"
    :class="{ active: tabName === activeMenuSelection, show: tabName === activeMenuSelection }"
    :id="tabName"
    role="tabpanel"
    :aria-labelledby="tabName + '-tab'"
  >
    <div class="card h-100">
      <div class="card-body">
        <div class="container" v-if="!isPendingTab">
          <div class="row">
            <div class="col-sm-10">
              <div class="collapse" :class="{ show: showFilters }" :id="filtersCollapseId">
                <div class="card card-body wallet-filter-panel">
                  <h5 class="card-title">{{ t("filtersLabel") }}</h5>
                  <label for="filterByOrderType">{{ t("orderTypeLabel") }}</label>
                  <select id="filterByOrderType" class="form-select form-select-sm mb15" v-model="selectedType">
                    <option value="0">{{ t("allOrderTypesLabel") }}</option>
                    <option value="1">{{ t("buysLabel") }}</option>
                    <option value="2">{{ t("sellsLabel") }}</option>
                  </select>
                  <label for="filterByOrderStatus">{{ t("orderStatusLabel") }}</label>
                  <select id="filterByOrderStatus" class="form-select form-select-sm mb15" v-model="selectedStatus">
                    <option value="0">{{ t("allOrderStatusesLabel") }}</option>
                    <option value="1">{{ t("processingLabel") }}</option>
                    <option value="2">{{ t("placedLabel") }}</option>
                    <option value="3">{{ t("cancelledLabel") }}</option>
                    <option value="4">{{ t("filledLabel") }}</option>
                  </select>
                  <label :for="exchangeFilterButtonId">{{ t("exchangeLabel") }}</label>
                  <div class="dropdown mb15">
                    <button
                      :id="exchangeFilterButtonId"
                      class="btn btn-light form-select form-select-sm order-exchange-select-button"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      :disabled="orderExchangeFilterDisabled"
                      :aria-disabled="orderExchangeFilterDisabled"
                    >
                      <span class="order-exchange-option-content">
                        <img :src="getSelectedExchangeLogo()" class="order-exchange-option-logo" alt="" />
                        <span class="order-exchange-option-label">{{ getSelectedExchangeLabel() }}</span>
                      </span>
                    </button>
                    <ul class="dropdown-menu order-exchange-dropdown-menu" :aria-labelledby="exchangeFilterButtonId">
                      <li>
                        <button class="dropdown-item order-exchange-dropdown-item" type="button" @click.prevent="selectOrderExchange(0)">
                          <img :src="exchangeLogos.ALLX" class="order-exchange-option-logo" alt="" />
                          <span>{{ getAllExchangesSelectLabel() }}</span>
                        </button>
                      </li>
                      <li v-for="exchange in exchanges" :key="exchange.eID">
                        <button class="dropdown-item order-exchange-dropdown-item" type="button" @click.prevent="selectOrderExchange(exchange.eID)">
                          <img :src="getExchangeLogo(exchange)" class="order-exchange-option-logo" alt="" />
                          <span>{{ getExchangeSelectLabel(exchange) }}</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                  <label for="filterByOrderPeriod">{{ t("periodLabel") }}</label>
                  <select id="filterByOrderPeriod" class="form-select form-select-sm mb15" v-model="selectedPeriod">
                    <option value="0">{{ t("thisMonthLabel") }}</option>
                    <option value="1">{{ t("past3MonthLabel") }}</option>
                    <option value="2">{{ t("past6MonthLabel") }}</option>
                    <option value="3">{{ t("pastYearLabel") }}</option>
                    <option value="4">YTD</option>
                    <option value="5">{{ t("fromDateLabel") }}</option>
                  </select>
                  <Datepicker
                    class="mb15"
                    v-if="5 === parseInt(filterOrdersByPeriod)"
                    v-model="selectedDate"
                    format="yyyy-MM-dd"
                    placeholder="yyyy-mm-dd"
                    :minDate="new Date(minDateForWallet)"
                    :maxDate="new Date()"
                    showNowButton
                  />
                  <button type="button" class="btn btn-primary w-100 mb15" @click.prevent="$emit('reset-filters')">
                    {{ t("clearLabel") }}
                  </button>
                </div>
              </div>
              <div class="collapse" :class="{ show: showSorting }" :id="sortingCollapseId">
                <div class="card card-body wallet-filter-panel">
                  <label for="sortingOrders">{{ t("sortingLabel") }}</label>
                  <select id="sortingOrders" class="form-select form-select-sm mb15" v-model="selectedSort">
                    <option value="0">{{ t("sortDateAscLabel") }}</option>
                    <option value="1">{{ t("sortDateDescLabel") }}</option>
                    <option value="2">{{ t("sortSymbolAscLabel") }}</option>
                    <option value="3">{{ t("sortSymbolDescLabel") }}</option>
                    <option value="4">{{ t("sortNameAscLabel") }}</option>
                    <option value="5">{{ t("sortNameDescLabel") }}</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-sm-2">
              <div class="wallet-tab-actions">
                <button
                  type="button"
                  class="btn btn-link"
                  :class="{ active: showFilters }"
                  :aria-expanded="showOrdersFilters"
                  :aria-controls="filtersCollapseId"
                  @click.prevent="toggleFilters"
                >
                  <i class="icon ion-md-options"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-link"
                  :class="{ active: showSorting }"
                  :aria-expanded="showOrdersSorting"
                  :aria-controls="sortingCollapseId"
                  @click.prevent="toggleSorting"
                >
                  <i class="icon ion-md-reorder"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="wallet-history">
          <div v-if="loading" class="wallet-history-loading">
            <Preloader />
          </div>
          <div v-else-if="1200 < windowsWidth" class="table-responsive">
            <table class="table is-full-width" :class="{ 'orders-table-compact': isPendingTab }">
              <thead>
                <tr>
                  <th>{{ t("orderLabel") }}</th>
                  <th class="orders-timestamp-column">{{ t("timestampLabel") }}</th>
                  <th>{{ t("typeLabel") }}</th>
                  <th class="orders-order-type-column">{{ orderTypeColumnLabel }}</th>
                  <th>{{ t("codeLabel") }}</th>
                  <th>{{ t("nameLabel") }}</th>
                  <th class="orders-quantity-column price-data">{{ quantityColumnLabel }}</th>
                  <th>{{ t("statusLabel") }}</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr class="orders-table-row" v-for="transaction in orders" :key="transaction.acquired">
                  <td>#{{ transaction.order_id }}</td>
                  <td class="orders-timestamp-column">
                    {{ getDate(transaction.acquired) }}
                  </td>
                  <td style="text-align:center">{{ getOrderType(transaction.action) }}</td>
                  <td class="orders-order-type-column" style="text-align:center">{{ capitalizeFirstLetter(transaction.type) }}</td>
                  <td style="text-align:center">{{ transaction.code.toUpperCase() }}</td>
                  <td>
                    <div v-if="0 === 'BG'.localeCompare(userLanguage)">
                      {{ transaction.nBG ? transaction.nBG : transaction.nEN }}
                    </div>
                    <div v-if="0 === 'EN'.localeCompare(userLanguage)">{{ transaction.nEN }}</div>
                  </td>
                  <td class="price-data orders-quantity-column">{{ formatOrderAmount(transaction.amount) }}</td>
                  <td :class="getOrderStatusClass(transaction)">{{ getOrderStatus(transaction) }}</td>
                  <td class="orders-inline-actions-cell">
                    <div class="btn-group orders-inline-actions" role="group" v-if="3 >= transaction.status">
                      <button class="btn orders-inline-action-button" type="button" @click.prevent="$emit('confirm-order-cancel', transaction.order_id)">
                        <svg class="orders-inline-action-icon" id="Component_172_4" data-name="Component 172 - 4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path id="Path_3614" data-name="Path 3614" d="M0,0H24V24H0Z" fill="none" />
                          <path
                            id="Path_3954"
                            data-name="Path 3954"
                            d="M10,10,22.317,22.317m0-12.317L10,22.317"
                            transform="translate(-4.158 -4.158)"
                            fill="none"
                            stroke="#000"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.5"
                          />
                        </svg>
                      </button>
                      <button class="btn orders-inline-action-button" type="button" @click.prevent="$emit('update-order', transaction)">
                        <svg class="orders-inline-action-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <g id="Component_171_1" data-name="Component 171 - 1" style="mix-blend-mode: normal;isolation: isolate">
                            <path id="Path_3612" data-name="Path 3612" d="M0,0H24V24H0Z" fill="none" />
                            <path
                              id="Path_3613"
                              data-name="Path 3613"
                              d="M4,20H8L18.5,9.5a2.828,2.828,0,0,0-4-4L4,16v4"
                              fill="none"
                              stroke="#000"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                            />
                            <line
                              id="Line_192"
                              data-name="Line 192"
                              x2="4"
                              y2="4"
                              transform="translate(13.5 6.5)"
                              fill="none"
                              stroke="#000"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                            />
                          </g>
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td class="orders-action-cell">
                    <div class="orders-action-content">
                      <button
                        type="button"
                        class="btn btn-primary"
                        :class="{ 'xsmall-font-size': 1200 > windowsWidth }"
                        @click.prevent="$emit('open-order-details', transaction)"
                      >
                        &rarr;
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else v-for="transaction in orders" :key="transaction.acquired">
            <div class="card h-100">
              <div class="card-body xsmall-font-size">
                <div class="container p-0">
                  <div class="row justify-content-between">
                    <div class="col-6">
                      #{{ transaction.order_id }}<br />
                      {{ getDate(transaction.acquired) }}<br />
                      {{ transaction.code.toUpperCase() }}@{{ transaction.xcode.toUpperCase() }}<br />
                      {{ getOrderStatus(transaction) }}
                    </div>
                    <div class="col-6 text-end my-auto">
                      <button
                        type="button"
                        class="btn btn-primary"
                        :class="{ 'xsmall-font-size': 1200 > windowsWidth }"
                        @click.prevent="$emit('open-order-details', transaction)"
                      >
                        &rarr;
                      </button>&nbsp;
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import allExchangesLogo from "@/assets/images/XALL-EF.svg";
import bfbLogo from "@/assets/images/XBUL.svg";
import deutscheBoerseLogo from "@/assets/images/Deutsche-Boerse_Logo.svg";
import Preloader from "@/components/ui/Preloader.vue";
import { getAllExchangesSelectLabel, getExchangeSelectLabel } from "@/assets/js/exchange_labels.js";

export default {
  name: "WalletOrdersTab",
  components: {
    Datepicker,
    Preloader
  },
  props: {
    activeMenuSelection: {
      type: String,
      required: true
    },
    tabName: {
      type: String,
      default: "Orders"
    },
    orders: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    windowsWidth: {
      type: Number,
      required: true
    },
    userLanguage: {
      type: String,
      required: true
    },
    minDateForWallet: {
      type: String,
      required: true
    },
    filterSelectedDate: {
      type: [Date, String, Number, Object],
      default: null
    },
    filterOrdersByType: {
      type: [Number, String],
      required: true
    },
    filterOrdersByStatus: {
      type: [Number, String],
      required: true
    },
    filterOrdersByExchange: {
      type: [Number, String],
      default: 0
    },
    filterOrdersByPeriod: {
      type: [Number, String],
      required: true
    },
    exchanges: {
      type: Array,
      default: () => []
    },
    orderExchangeFilterDisabled: {
      type: Boolean,
      default: false
    },
    sortOrders: {
      type: [Number, String],
      required: true
    },
    showOrdersFilters: {
      type: Boolean,
      required: true
    },
    showOrdersSorting: {
      type: Boolean,
      required: true
    },
    translator: {
      type: Function,
      required: true
    },
    priceFormatter: {
      type: Function,
      required: true
    },
    bondsPriceFormatter: {
      type: Function,
      required: true
    },
    dateFormatter: {
      type: Function,
      required: true
    },
    shortDateFormatter: {
      type: Function,
      required: true
    },
    capitalizeFormatter: {
      type: Function,
      required: true
    },
    orderTypeFormatter: {
      type: Function,
      required: true
    },
    orderStatusFormatter: {
      type: Function,
      required: true
    },
    stockTypeResolver: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      exchangeLogos: {
        ALLX: allExchangesLogo,
        XBUL: bfbLogo,
        XETR: deutscheBoerseLogo,
        XFRA: deutscheBoerseLogo
      }
    };
  },
  emits: [
    "update:filterSelectedDate",
    "update:filterOrdersByType",
    "update:filterOrdersByStatus",
    "update:filterOrdersByExchange",
    "update:filterOrdersByPeriod",
    "update:sortOrders",
    "update:showOrdersFilters",
    "update:showOrdersSorting",
    "reset-filters",
    "confirm-order-cancel",
    "update-order",
    "open-order-details"
  ],
  computed: {
    selectedDate: {
      get() {
        return this.filterSelectedDate;
      },
      set(value) {
        this.$emit("update:filterSelectedDate", value);
      }
    },
    selectedType: {
      get() {
        return this.filterOrdersByType;
      },
      set(value) {
        this.$emit("update:filterOrdersByType", value);
      }
    },
    selectedStatus: {
      get() {
        return this.filterOrdersByStatus;
      },
      set(value) {
        this.$emit("update:filterOrdersByStatus", value);
      }
    },
    selectedExchange: {
      get() {
        return this.filterOrdersByExchange;
      },
      set(value) {
        this.$emit("update:filterOrdersByExchange", value);
      }
    },
    selectedPeriod: {
      get() {
        return this.filterOrdersByPeriod;
      },
      set(value) {
        this.$emit("update:filterOrdersByPeriod", value);
      }
    },
    selectedSort: {
      get() {
        return this.sortOrders;
      },
      set(value) {
        this.$emit("update:sortOrders", value);
      }
    },
    showFilters() {
      return true === this.showOrdersFilters;
    },
    showSorting() {
      return true === this.showOrdersSorting;
    },
    filtersCollapseId() {
      return `collapse${this.tabName}Filters`;
    },
    sortingCollapseId() {
      return `collapse${this.tabName}Sorting`;
    },
    exchangeFilterButtonId() {
      return `filterBy${this.tabName}Exchange`;
    },
    isPendingTab() {
      return "Pending" === this.tabName;
    },
    orderTypeColumnLabel() {
      return 0 === "BG".localeCompare(this.userLanguage) ? "Поръчка" : this.t("orderTypeLabel");
    },
    quantityColumnLabel() {
      return 0 === "BG".localeCompare(this.userLanguage) ? "К-во" : this.t("quantityLabel");
    }
  },
  methods: {
    t(key, ...args) {
      return this.translator(key, ...args);
    },
    formatPrice(...args) {
      return this.priceFormatter(...args);
    },
    formatBondsPrice(...args) {
      return this.bondsPriceFormatter(...args);
    },
    getDate(...args) {
      return this.dateFormatter(...args);
    },
    getShortDate(...args) {
      return this.shortDateFormatter(...args);
    },
    capitalizeFirstLetter(...args) {
      return this.capitalizeFormatter(...args);
    },
    getOrderType(...args) {
      return this.orderTypeFormatter(...args);
    },
    getOrderStatus(...args) {
      return this.orderStatusFormatter(...args);
    },
    getOrderStatusClass(order) {
      const status = parseInt(order?.status);

      if ([6, 7].includes(status)) {
        return "order-status-filled";
      }

      if ([4, 5].includes(status)) {
        return "order-status-cancelled";
      }

      return "order-status-default";
    },
    getStockTypeId(...args) {
      return this.stockTypeResolver(...args);
    },
    formatOrderAmount(amount) {
      return this.formatNumberToMaximumDecimals(amount, 4);
    },
    formatNumberToMaximumDecimals(value, maxDecimals) {
      const numberValue = parseFloat(value);

      if (!Number.isFinite(numberValue)) {
        return value;
      }

      return numberValue.toLocaleString("en-US", {
        maximumFractionDigits: maxDecimals
      });
    },
    selectOrderExchange(exchangeId) {
      this.selectedExchange = parseInt(exchangeId);
    },
    getSelectedExchange() {
      return this.exchanges.find(exchange => parseInt(exchange.eID) === parseInt(this.selectedExchange));
    },
    getSelectedExchangeLogo() {
      const exchange = this.getSelectedExchange();
      return exchange ? this.getExchangeLogo(exchange) : this.exchangeLogos.ALLX;
    },
    getExchangeLogo(exchange) {
      const code = exchange?.code?.toString().toUpperCase() || "";
      return this.exchangeLogos[code] || this.exchangeLogos.ALLX;
    },
    getSelectedExchangeLabel() {
      const exchange = this.getSelectedExchange();
      return exchange ? this.getExchangeSelectLabel(exchange) : this.getAllExchangesSelectLabel();
    },
    getAllExchangesSelectLabel() {
      return getAllExchangesSelectLabel(this.userLanguage);
    },
    getExchangeSelectLabel(exchange) {
      return getExchangeSelectLabel(exchange, this.userLanguage);
    },
    toggleFilters() {
      this.$emit("update:showOrdersFilters", !this.showOrdersFilters);
      this.$emit("update:showOrdersSorting", false);
    },
    toggleSorting() {
      this.$emit("update:showOrdersSorting", !this.showOrdersSorting);
      this.$emit("update:showOrdersFilters", false);
    }
  }
};
</script>

<style scoped>
.wallet-tab-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}

.wallet-tab-actions .btn-link {
  align-items: center;
  border: 1px solid transparent;
  display: inline-flex;
  justify-content: center;
  min-height: 2rem;
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
}

.wallet-tab-actions .btn-link:focus,
.wallet-tab-actions .btn-link:focus-visible {
  box-shadow: none;
  outline: 0;
}

.wallet-tab-actions .btn-link.active {
  border-color: #0088aa;
}

.wallet-history-loading {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 8rem;
}

.orders-table-row > td {
  vertical-align: middle !important;
}

.orders-action-cell {
  text-align: center;
  vertical-align: middle !important;
}

.orders-action-content {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}

.orders-quantity-column {
  max-width: 6rem;
  min-width: 5rem;
  white-space: nowrap;
  width: 5rem;
}

.price-data {
  text-align: right;
  white-space: nowrap;
}

.orders-timestamp-column {
  min-width: 9.25rem;
  white-space: nowrap;
}

.orders-inline-actions-cell {
  padding-left: 0.15rem !important;
  padding-right: 0.15rem !important;
  text-align: center;
  vertical-align: middle !important;
  width: 4.25rem;
}

.orders-inline-actions {
  align-items: center;
  display: inline-flex;
  gap: 0.2rem;
  justify-content: center;
  margin: 0;
}

.orders-inline-action-button {
  align-items: center;
  border: 0;
  display: inline-flex;
  height: 1.65rem;
  justify-content: center;
  padding: 0;
  width: 1.65rem;
}

.orders-inline-action-icon {
  height: 1rem;
  width: 1rem;
}

.orders-table-compact {
  font-size: 0.86rem;
}

.orders-table-compact th,
.orders-table-compact td {
  padding-left: 0.45rem;
  padding-right: 0.45rem;
}

.orders-table-compact td {
  white-space: nowrap;
}

.orders-table-compact td:nth-child(6) {
  white-space: normal;
}

.orders-order-type-column {
  min-width: 5.5rem;
  white-space: nowrap;
}

.order-status-filled {
  color: green;
}

.order-status-cancelled {
  color: red;
}

.order-status-default {
  color: black;
}

.order-exchange-select-button {
  align-items: center;
  background-color: #fff;
  border: 1px solid #ced4da;
  display: flex;
  height: 2rem;
  padding-right: 2rem;
  text-align: left;
  width: 100%;
}

.order-exchange-select-button:disabled {
  background-color: #e9ecef;
  border-color: #d3d8de;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 1;
}

.order-exchange-select-button:disabled .order-exchange-option-logo {
  opacity: 0.55;
}

.order-exchange-option-content {
  align-items: center;
  display: flex;
  gap: 0.45rem;
  min-width: 0;
  width: 100%;
}

.order-exchange-option-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-exchange-option-logo {
  flex: 0 0 auto;
  height: 1rem;
  object-fit: contain;
  width: 1rem;
}

.order-exchange-dropdown-menu {
  border-radius: 0;
  min-width: 100%;
  padding: 0;
}

.order-exchange-dropdown-item {
  align-items: center;
  display: flex;
  gap: 0.45rem;
  min-height: 2.6rem;
}
</style>
